import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useState, createElement } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../../common/api/apiShared";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { IAddEditContractTypeDialogProps } from "./AddEditContractTypeDialog";
import { Result } from "../../../common/api/result";
import { SaveContractTypeViewModel } from "../contractTypes/ContractType.types.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAddEditContractTypesDialogHooks(props: IAddEditContractTypeDialogProps) {
    const { translateString } = useTranslation();
    const [saveModel, setSaveModel] = useState<SaveContractTypeViewModel>({ name: props.ContractTypeToUpdate?.name ?? "" });

    const snackBar = useDotLegalSnackbar();

    const validation = useValidator<SaveContractTypeViewModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        [saveModel]
    );
    validation.check(saveModel);

    const onModelChange = (updateFunc: (model: SaveContractTypeViewModel) => void) => {
        let tempSaveModel = { ...saveModel! };

        updateFunc(tempSaveModel);
        setSaveModel(tempSaveModel);
    };

    const saveMutation = useDotLegalMutation(addContractType, {
        onSuccess: (response: Result<string>) => {
            props.onSaved();
            snackBar.show(createElement(Trans, { i18nKey: "itemCreatedSuccess", values: { name: saveModel.name } }));
        },
    });

    const updateMutation = useDotLegalMutation(updateContractType, {
        onSuccess: (response: Result<string>) => {
            snackBar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: saveModel.name } }));
        },
    });

    const onSave = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (props.isAdd) {
            await saveMutation.mutateAsync();
        } else {
            await updateMutation.mutateAsync(props.ContractTypeToUpdate!.id);
        }

        props.onSaved();
    };

    function addContractType() {
        return post<string>("/ContractTypes", saveModel);
    }

    function updateContractType(id: string) {
        return put<string>(`/ContractTypes/${id}`, saveModel);
    }

    return {
        isSaving: saveMutation.isLoading || updateMutation.isLoading,
        validation,
        onModelChange,
        saveModel,
        onSave,
    };
}
